import { useState, useEffect, useRef, useContext } from 'react';
import io from 'socket.io-client';

import * as JKYModel from '../../custom-types';
import api, { socketRoot, platform } from '../../utils/Api';
import { authContext } from '../../contexts/AuthContext';
import useIntervalHandler from './IntervalHandler';
import { LiveAdminMessage, adminMessagesFromServer, currentAdminMessage, nextAdminMessageEnd } from '../AdminMessages';
import { ScreenKind, isOutdated, scheduleReload } from '../SiteVersion';
import { LiveShoutout, currentPopup, fromServer, nextShoutoutChange, stillShowing, tickerShoutouts } from '../Shoutouts';

// How the player screen learns that its queue changed.
//
// It used to re-fetch the whole player every five seconds and nothing else, which meant a
// video added from a phone took up to five seconds to show up, the screen sat on
// "Launching..." for the first five of those because the interval has no leading call, and
// the venue's browser made twelve requests a minute forever.
//
// Now the server pushes. Polling stays, deliberately, as the fallback: venue wifi and hotel
// captive portals block WebSockets often enough that a player which only listened on a
// socket would freeze on the last frame it happened to receive, and nobody is standing at
// the screen to reload it. So the poll never stops, it just slows down to a safety net
// while the socket is up.
const CONNECTED_POLL_MS = 30000;
const DISCONNECTED_POLL_MS = 5000;

// Stamps a trivia state with how far the server's clock is from ours, so every screen counts
// down to the same moment whatever its own clock says.
const withClock = (state: JKYModel.TriviaState): JKYModel.TriviaState =>
  state && state.now ? { ...state, clockOffset: state.now - Date.now() } : state;

// How long a page that finds itself out of date spreads its reload over. Matches the
// server's spread for a reload a site admin asks for.
const VERSION_RELOAD_SPREAD_MS = 8000;

// `screen` says whether this is the TV on the wall or a phone, which decides how it reloads
// when it is asked to or finds it is out of date (utils/SiteVersion.ts).
const usePlayerUpdateHandler = (playerIdentifier: string, screen: ScreenKind = 'phone') => {
  const [player, setPlayer] = useState<JKYModel.Player>();
  // Trivia night rides the same socket and the same fallback poll as the player.
  const [trivia, setTrivia] = useState<JKYModel.TriviaState>();
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const context = useContext(authContext);
  const token = context.auth.token;
  const socketRef = useRef(null);
  // Paid shout-outs on the TV now. Same socket and same fallback poll again.
  const [shoutouts, setShoutouts] = useState<LiveShoutout[]>([]);

  const fetchShoutouts = () =>
    api
      .shoutouts(playerIdentifier, 'GET', token, null)
      .then(response => setShoutouts(fromServer(response && response.shoutouts)))
      .catch(err => console.log('There has been a problem with your getShoutouts operation: ' + err.message));

  // Messages from a Jookable site admin. Same socket and same fallback poll again.
  const [adminMessages, setAdminMessages] = useState<LiveAdminMessage[]>([]);

  const fetchAdminMessages = () =>
    api
      .adminMessages(playerIdentifier, 'GET', token, null)
      .then(response => setAdminMessages(previous => adminMessagesFromServer(response && response.messages, previous)))
      .catch(err => console.log('There has been a problem with your getAdminMessages operation: ' + err.message));

  const fetchTrivia = () =>
    api
      .trivia(playerIdentifier, 'GET', token, null)
      .then(response => setTrivia(withClock(response)))
      .catch(err => console.log('There has been a problem with your getTrivia operation: ' + err.message));

  const fetchData = () => {
    fetchTrivia();
    fetchShoutouts();
    fetchAdminMessages();
    return api
      .players(`identifier/${playerIdentifier}`, 'GET', token, null)
      .then(response => {
        setPlayer(response);
        setError(null);
      })
      .catch(err => {
        console.log('There has been a problem with your getPlayer operation: ' + err.message);
        setError(err);
      });
  };

  // Fetch once immediately rather than waiting out a first interval. This is what takes the
  // "Launching..." screen from about five seconds to as long as one request takes.
  useEffect(() => {
    fetchData();
  }, [playerIdentifier, token]);

  useEffect(() => {
    if (!playerIdentifier || !token) {
      return;
    }

    // The server authenticates the handshake with the same token and session row the REST
    // routes check, so a socket is not a way around the API's auth.
    // The token goes in the handshake's auth payload rather than the URL, so it never lands
    // in a proxy or server access log.
    const socket = io(socketRoot, {
      auth: { token, platform },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('player:subscribe', { playerIdentifier });
    });

    socket.on('player:update', (updatedPlayer: JKYModel.Player) => {
      setPlayer(updatedPlayer);
      setError(null);
    });

    // The broadcast leaves out `me`, the caller's own answer, which only the GET carries,
    // so it is kept from the last read until a new question clears it.
    socket.on('trivia:update', (state: JKYModel.TriviaState) => {
      setTrivia(previous => {
        const next = withClock(state);
        const sameQuestion = previous && previous.me && previous.questionNumber === next.questionNumber;
        return { ...next, me: sameQuestion ? previous.me : previous && previous.me ? { ...previous.me, answerIndex: null } : null };
      });
    });

    socket.on('shoutouts:update', (payload: { shoutouts: any[] }) => {
      setShoutouts(fromServer(payload && payload.shoutouts));
    });

    // The server says which build of the web app it has as each socket connects, so a page
    // still open from before an update reloads onto the new one.
    socket.on('site:version', (payload: { version: string | null }) => {
      if (isOutdated(payload && payload.version)) {
        scheduleReload(screen, VERSION_RELOAD_SPREAD_MS, 'version');
      }
    });

    // A Jookable site admin asked this jukebox's screens, or every screen, to reload.
    socket.on('site:reload', (payload: { spreadMs?: number }) => {
      const spreadMs = payload && typeof payload.spreadMs === 'number' ? Math.min(payload.spreadMs, 30000) : 8000;
      scheduleReload(screen, spreadMs, 'admin');
    });

    socket.on('admin-messages:update', (payload: { messages: any[] }) => {
      setAdminMessages(previous => adminMessagesFromServer(payload && payload.messages, previous));
    });

    // The network went away, or the handshake was refused. Both mean the same thing here:
    // stop trusting the socket and let the fast poll carry the screen.
    // A refused handshake also lands in connect_error. Socket.IO 4 does not retry one on its
    // own, which is right: a refused token will be refused again, so the poll carries on, gets
    // a 401 and sends the user back to the login screen.
    socket.on('disconnect', (reason: string) => {
      setConnected(false);
      // Every other reason reconnects on its own. This one is the server ending the socket on
      // purpose, which Socket.IO leaves to us; try again rather than living on the poll.
      if (reason === 'io server disconnect') {
        socket.connect();
      }
    });
    socket.on('connect_error', () => setConnected(false));

    return () => {
      socket.removeAllListeners();
      socket.close();
      socketRef.current = null;
      setConnected(false);
    };
  }, [playerIdentifier, token]);

  useIntervalHandler(fetchData, connected ? CONNECTED_POLL_MS : DISCONNECTED_POLL_MS);

  // A new shout-out pops up big for a few seconds, then drops into the ticker, and comes off
  // the screen when its time is up, all without waiting for the server. `tick` re-renders at
  // each of those moments.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const next = nextShoutoutChange(shoutouts);
    if (next === null) {
      return;
    }
    const timer = window.setTimeout(() => {
      setShoutouts(current => {
        const showing = stillShowing(current);
        return showing.length === current.length ? current : showing;
      });
      setTick(value => value + 1);
    }, Math.max(0, next - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [shoutouts, tick]);

  // An admin message comes off the screen when its time is up, without waiting for the server.
  const [adminTick, setAdminTick] = useState(0);
  useEffect(() => {
    const next = nextAdminMessageEnd(adminMessages);
    if (next === null) {
      return;
    }
    const timer = window.setTimeout(() => {
      setAdminMessages(current => current.filter(message => message.endsAt > Date.now()));
      setAdminTick(value => value + 1);
    }, Math.max(0, next - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [adminMessages, adminTick]);

  const now = Date.now();
  return {
    player,
    error,
    connected,
    trivia,
    setTrivia,
    refreshTrivia: fetchTrivia,
    // In the ticker now.
    shoutouts: tickerShoutouts(shoutouts, now),
    // Popping up now, or null.
    popup: currentPopup(shoutouts, now),
    // A site admin's message on the screen now, or null.
    adminMessage: currentAdminMessage(adminMessages, now),
  };
};

export default usePlayerUpdateHandler;
